import {DotYouClient, UploadResult} from "@homebase-id/js-lib/core";
import {getNewId, jsonStringify64, uint8ArrayToBase64} from "@homebase-id/js-lib/helpers";
import {makeAutoObservable, runInAction} from "mobx";
import moment from "moment/moment";
import {
    HomebaseStorage,
    Image64Data,
    ImageHeader,
    SaveFileConfig,
    SaveOptions
} from "../core/storage/HomebaseStorageTypes";
import {GuideFlowConfig} from "../../system/GuideFlowConfig";
import {SimplifiedStorageProvider} from "../core/storage/SimplifiedStorageProvider";
import {CassiePayloadResult, CassieSavePayloadData} from "../core/cassie/CassieTypes";
import {format} from 'date-fns';
import {base64ToUint8Array, byteArrayToString, stringToUint8Array} from "@homebase-id/js-lib/helpers";

const fileConfig: SaveFileConfig = {
    dataType: 2844,
    fileType: 1144,
    targetDrive: GuideFlowConfig.DataDrive
}

export const JournalPayloadKeys = {
    primaryImage: "journalimg",
    journalContent: "jcontent"
}


export interface JournalEntry extends HomebaseStorage {
    id: string;
    title: string;
}

export interface JournalContent {
    jsonContent: string;
}

export class JournalProvider {
    private static instance: JournalProvider
    private readonly provider: SimplifiedStorageProvider<JournalEntry>;
    private readonly dotYouClient: DotYouClient;
    public StateChanged: number = 0;

    constructor(config: SaveFileConfig, dotYouClient: DotYouClient) {
        this.dotYouClient = dotYouClient;
        this.provider = new SimplifiedStorageProvider<JournalEntry>(config, dotYouClient);
        makeAutoObservable(this);
        this.synchronize = this.synchronize.bind(this);
    }

    static getInstance(dotYouClient: DotYouClient) {
        if (JournalProvider.instance == null) {
            JournalProvider.instance = new JournalProvider(fileConfig, dotYouClient);
        }
        return JournalProvider.instance;
    }

    public async getAll(includeArchived?: boolean): Promise<JournalEntry[]> {
        return await this.provider.getAll({includeArchived: includeArchived});
    }

    public async exportGraph() {
        return await this.provider.exportGraph("journal");
    }

    public async getList(list: string[]): Promise<JournalEntry[]> {
        return await this.provider.getList(list);
    }

    public async syncFileType(): Promise<number> {
        const count = await this.provider.syncFileType(moment.utc().unix());
        if (count > 0) {
            this.notifyStateChange();
        }
        return count;
    }

    public async getAllGrouped(includeArchived?: boolean): Promise<Record<string, JournalEntry[]>> {
        const groupedEntries: Record<string, JournalEntry[]> = {};
        const entries = await this.getAll(includeArchived);

        const sorted = entries.sort((a, b) => {
            const aTime = a.storage?.created ?? 0;
            const bTime = b.storage?.created ?? 0;
            return bTime - aTime; // Descending
        });

        sorted.forEach(entry => {
            const created = entry.storage?.created;
            if (!created) return;

            const dateKey = format(new Date(created), 'yyyy-MM-dd');

            if (!groupedEntries[dateKey]) {
                groupedEntries[dateKey] = [];
            }
            groupedEntries[dateKey].push(entry);
        });

        return groupedEntries;
    }

    public async saveProfileImage(contactId: string, versionTag: string, image: Image64Data) {
        const header: ImageHeader = {
            width: image.width,
            height: image.height,
            mimeType: image.mimeType
        }

        const payload: CassieSavePayloadData = {
            key: JournalPayloadKeys.primaryImage,
            contentType: image.mimeType,
            descriptorContent: jsonStringify64(header),
            thumbnails: [],
            content: image.base64Data
        };

        await this.provider.savePayload(contactId, versionTag, payload);
        this.notifyStateChange();
    }

    public async getProfileImage(contactId: string): Promise<Image64Data | null> {
        return await this.provider.getImagePayload(contactId, JournalPayloadKeys.primaryImage);
    }

    public async save(entry: JournalEntry, content: JournalContent): Promise<string> {
        const options: SaveOptions =
            {
                groupId: undefined,
                userDate: undefined,
                onVersionConflict: () => {
                    //TODO: how to handle this?
                }
            };

        if (!entry.id) {
            entry.id = getNewId();
        }

        const payload = await this.createJournalPayload(content);
        const result: UploadResult | void = await this.provider.saveHeaderFile(entry, payload, options);

        if (result) {
            entry.storage.versionTag = result.newVersionTag;
            entry.storage.fileId = result.file.fileId;
            this.notifyStateChange();
        }

        return entry.id;
    }

    public async delete(id: string): Promise<boolean> {
        const result = await this.provider.setArchived(id, true);
        if (result) {
            this.notifyStateChange();
        }

        return result;
    };

    public async undelete(id: string): Promise<boolean> {
        const result = await this.provider.setArchived(id, false);
        if (result) {
            this.notifyStateChange();
        }

        return result;
    };

    public async get(entryId: string): Promise<JournalEntry | null> {
        return await this.provider.getByUniqueId(entryId);
    }

    public async getContent(entryId: string): Promise<JournalContent | null> {
        const result: CassiePayloadResult = await this.provider.getPayload(entryId, JournalPayloadKeys.journalContent);

        console.log('jp', result);
        if(!result?.content64) {
            console.error("content64 is null");
            return null;
        }
        
        const bytes = base64ToUint8Array(result.content64);
        const json = byteArrayToString(bytes);

        return {
            jsonContent: json
        };
    }

    public async synchronize(asOfTime: number) {
        const count = await this.provider.syncFileType(asOfTime);
        if (count > 0) {
            this.notifyStateChange();
        }
    }

    private notifyStateChange() {
        runInAction(() => {
            this.StateChanged++;
        });
    }

    private async createJournalPayload(content: JournalContent): Promise<CassieSavePayloadData> {
        const v = uint8ArrayToBase64(stringToUint8Array(jsonStringify64(content.jsonContent)));
        return {
            key: JournalPayloadKeys.journalContent,
            contentType: "application/json",
            descriptorContent: "",
            thumbnails: [],
            content: v
        };
    }
}